import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { deviceId, errText, fetchNotes, postNote } from "../../lib/api";

const MAX = 300;
const GRAIN =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.45 0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0.09 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";
export const PAPER = { backgroundColor: "#FFFAF2", backgroundImage: `${GRAIN}, linear-gradient(160deg, #FFFDF8 0%, #FBF1E6 100%)` };
const LINES = { backgroundImage: "repeating-linear-gradient(transparent 0 33px, rgba(126,87,194,0.14) 33px 34px)", backgroundAttachment: "local" };

function Envelope() {
  return (
    <svg viewBox="0 0 120 84" className="h-full w-full drop-shadow-[0_12px_18px_rgba(126,87,194,0.35)]" aria-hidden>
      <defs>
        <linearGradient id="env-body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FCE4F1" />
          <stop offset="0.55" stopColor="#EADDFB" />
          <stop offset="1" stopColor="#D6ECFB" />
        </linearGradient>
        <linearGradient id="env-flap" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F8BBD9" />
          <stop offset="1" stopColor="#CDB8F0" />
        </linearGradient>
      </defs>
      <rect x="2" y="10" width="116" height="72" rx="7" fill="url(#env-body)" stroke="#fff" strokeWidth="2" />
      <path d="M5 79 L60 44 L115 79" fill="none" stroke="#fff" strokeOpacity="0.9" strokeWidth="2" />
      <path d="M4 13 L60 53 L116 13" fill="url(#env-flap)" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
      <path d="M60 58 C55 52 49 55 52 60 L60 67 L68 60 C71 55 65 52 60 58 Z" fill="#D4AF37" />
    </svg>
  );
}

function Folded({ tilt }) {
  return (
    <svg viewBox="0 0 48 36" className="h-9 w-12" style={{ transform: `rotate(${tilt}deg)` }} aria-hidden>
      <rect x="1" y="1" width="46" height="34" rx="3" fill="#FFFAF2" stroke="#E8D9F5" strokeWidth="1.5" />
      <path d="M1 1 L24 19 L47 1" fill="none" stroke="#D9C6EE" strokeWidth="1.5" />
      <path d="M24 19 L24 35" stroke="#F1E6F8" strokeWidth="1" />
    </svg>
  );
}

function Letter({ text, setText, hint, busy, onSend, onClose }) {
  const ref = useRef(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <motion.div
      key="letter"
      data-testid="memory-letter"
      initial={{ opacity: 0, scaleY: 0.15, y: 24 }}
      animate={{ opacity: 1, scaleY: 1, y: 0 }}
      exit={{ opacity: 0, scaleY: 0.08, y: 36, transition: { duration: 0.55, ease: "easeIn" } }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      style={{ ...PAPER, transformOrigin: "50% 100%" }}
      className="relative w-full max-w-sm rounded-[6px] px-7 pb-5 pt-7 shadow-[0_22px_44px_-22px_rgba(91,58,133,0.55)]"
    >
      <button data-testid="memory-letter-close" onClick={onClose} aria-label="Fold the letter away" className="font-hand absolute right-4 top-2 text-2xl text-[#A895BF] transition-colors hover:text-[#7E57C2]">
        ×
      </button>
      <p data-testid="memory-letter-prompt" className="font-hand text-[30px] leading-tight text-[#5B3A85]">
        What did this remind you of?
      </p>
      <textarea
        ref={ref}
        data-testid="memory-letter-text"
        aria-label="Your memory"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        className="font-hand mt-3 w-full resize-none border-0 bg-transparent p-0 text-[25px] leading-[34px] text-[#3B2656] shadow-none outline-none ring-0 focus:outline-none focus:ring-0"
        style={LINES}
      />
      <div className="mt-2 flex min-h-[2rem] items-center justify-between gap-3">
        <span className="font-hand text-lg text-[#C2185B]/75" data-testid="memory-letter-hint" role="status">
          {hint || (text.length > 260 ? `${MAX - text.length} left` : "")}
        </span>
        <button data-testid="memory-letter-send" disabled={busy} onClick={onSend} className="font-hand shrink-0 text-[28px] text-[#7E57C2] underline-offset-[6px] transition-[color,transform] hover:-translate-y-0.5 hover:underline disabled:opacity-50">
          {busy ? "sealing…" : "Send"}
        </button>
      </div>
    </motion.div>
  );
}

function Memories({ notes }) {
  const [open, setOpen] = useState(null);
  if (!notes.length) return null;
  return (
    <div data-testid="memories" className="flex flex-wrap items-start gap-4 pt-2">
      {notes.map((n, i) => {
        const isOpen = open === n.id;
        const text = n.text || (n.feelings || []).join(" · ");
        return (
          <motion.button
            key={n.id}
            layout
            data-testid={`memory-letter-${n.id}`}
            aria-expanded={isOpen}
            aria-label={isOpen ? "Fold this memory" : "Open a memory left here"}
            onClick={() => setOpen(isOpen ? null : n.id)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: isOpen ? 0 : [0, -7, 0] }}
            transition={{
              opacity: { duration: 1, delay: i * 0.12 },
              y: isOpen ? { duration: 0.4 } : { duration: 6 + (i % 4), repeat: Infinity, ease: "easeInOut", delay: i * 0.5 },
              layout: { duration: 0.45 },
            }}
            className="text-left"
          >
            {isOpen ? (
              <span data-testid={`memory-open-${n.id}`} style={PAPER} className="font-hand block max-w-[16rem] whitespace-pre-wrap break-words rounded-[5px] px-4 py-3 text-[22px] leading-snug text-[#3B2656] shadow-[0_14px_30px_-16px_rgba(91,58,133,0.5)]">
                {text}
              </span>
            ) : (
              <Folded tilt={((i % 5) - 2) * 4} />
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

export function EnvelopeMemories({ painting, onSent }) {
  const [phase, setPhase] = useState("idle");
  const [text, setText] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const timers = useRef([]);

  useEffect(() => {
    if (painting.id) fetchNotes(painting.id).then(setNotes).catch(() => setNotes([]));
    const list = timers.current;
    return () => list.forEach(clearTimeout);
  }, [painting.id]);

  const write = (v) => {
    setText(v);
    setHint("");
  };
  const send = async () => {
    const t = text.trim();
    if (!t) return setHint("Write a few words first, whenever you're ready.");
    if (t.length > MAX) return setHint("A little shorter, please. Only 300 characters fit in the envelope.");
    setBusy(true);
    try {
      const note = await postNote(painting.id, { text: t, device_id: deviceId() });
      onSent?.(t);
      setPhase("sending");
      timers.current.push(
        setTimeout(() => {
          setPhase("sent");
          setText("");
          if (!note.held) setNotes((l) => [note, ...l].slice(0, 12));
        }, 1900),
        setTimeout(() => setPhase((p) => (p === "sent" ? "idle" : p)), 5200),
      );
    } catch (e) {
      setHint(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="memories-area" className="flex min-h-[7rem] flex-col gap-5">
      <AnimatePresence mode="wait">
        {phase === "idle" && (
          <motion.button
            key="env"
            data-testid="memory-envelope"
            aria-label="Leave a memory in the envelope"
            onClick={() => setPhase("open")}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: [0, -6, 0], rotate: [-2, 2, -2] }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.3 } }}
            transition={{ opacity: { duration: 0.8 }, y: { duration: 5, repeat: Infinity, ease: "easeInOut" }, rotate: { duration: 7, repeat: Infinity, ease: "easeInOut" } }}
            whileHover={{ scale: 1.06 }}
            className="group flex items-center gap-3 self-start"
          >
            <span className="block h-14 w-20">
              <Envelope />
            </span>
            <span className="font-hand text-xl text-[#7C6992] opacity-70 transition-opacity group-hover:opacity-100">a memory?</span>
          </motion.button>
        )}
        {phase === "open" && <Letter text={text} setText={write} hint={hint} busy={busy} onSend={send} onClose={() => setPhase("idle")} />}
        {phase === "sending" && (
          <motion.span
            key="fly"
            data-testid="memory-envelope-sending"
            className="block h-14 w-20 self-start"
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.7], y: [10, 0, 0, -170], x: [0, 0, 0, 60], rotate: [0, 0, -4, -12] }}
            transition={{ duration: 1.35, times: [0, 0.2, 0.45, 1], ease: "easeInOut" }}
          >
            <Envelope />
          </motion.span>
        )}
        {phase === "sent" && (
          <motion.p key="thanks" data-testid="memory-thank-you" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="font-hand text-3xl text-[#7E57C2]">
            Thank you for sharing
          </motion.p>
        )}
      </AnimatePresence>
      <Memories notes={notes} />
    </div>
  );
}
